import { Close, InfoOutlined } from "@mui/icons-material";
import { Alert, Box, IconButton, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DateTime } from "luxon";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import RecommendationsDrawer from "../../components/RecommendationsDrawer/RecommendationsDrawer";
import { DRAWER_WIDTH_IN_PX } from "../../components/RecommendationsDrawer/utils";
import { WageBreakdownReopenProvider } from "../../components/WageBreakdownReturn/WageBreakdownReopenContext";
import { useDraftMode } from "../../contexts/DraftModeContext";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useRecommendationsDrawerContext } from "../../contexts/RecommendationsDrawerContext";
import { useSelfScheduling } from "../../contexts/SelfScheduling/SelfSchedulingContext";
import { useUserSettingsContext } from "../../contexts/Staffing/UserSettingsContext";
import { useWeekIndex } from "../../contexts/WeekIndexProvider";
import { useWeekViewContext } from "../../contexts/WeekView";
import { useArrivalState } from "../../hooks/useArrivalState";
import { INK, STATUS, STATUS_BADGE } from "../../theme/tokens";
import { type BaseNurseShift, type Nurse, RecommendationOpenSource } from "../../utils/models";
import { parseWageBreakdownReopen, REOPEN_WAGE_BREAKDOWN_STATE_KEY, type WageBreakdownReopen } from "../../utils/navigation/wageBreakdownReturn";
import { getUniqueLocations } from "../../utils/utilFunctions";
import { DayScheduleContentWrapper } from "./DayScheduleContentWrapper";
import { DayScheduleHeader } from "./Header/DayScheduleHeader";
import { useDayScheduleStaffingIssues } from "./hooks";

export const DaySchedule: FC = () => {
	const { date } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const { t } = useTranslation();
	const { getWeekIndexByDate, getDayIndexByDate, chosenDepartment } = useHospitalData();
	const { setWeekIndex } = useWeekIndex();
	const { isOpen, toggleDrawerOpen } = useRecommendationsDrawerContext();
	const { weekViewData } = useWeekViewContext();
	const { isDataInSelfSchedulingPeriod } = useSelfScheduling();
	const { settings, updateSettings } = useUserSettingsContext();
	const { isDraftMode } = useDraftMode();
	const [showDraftBanner, setShowDraftBanner] = useState<boolean>(true);
	const [showStaffVisibilityBanner, setShowStaffVisibilityBanner] = useState<boolean>(true);
	const [requestIdToScroll, setRequestIdToScroll] = useState<string | null>(null);

	// Parse the date from URL parameter (format: yyyy-MM-dd)
	const luxonDate = useMemo<DateTime>(
		() => (date ? DateTime.fromISO(date) : DateTime.now()).setZone(chosenDepartment.timezone),
		[date, chosenDepartment.timezone],
	);
	const weekIndex = useMemo<number>(() => getWeekIndexByDate(luxonDate), [luxonDate, getWeekIndexByDate]);
	const dayIndex = useMemo<number>(() => getDayIndexByDate(luxonDate), [luxonDate, getDayIndexByDate]);
	const [selectedNurse, setSelectedNurse] = useState<Nurse | null>(null);

	const isDateNotVisibleToStaff = useMemo<boolean>(() => {
		if (!chosenDepartment.staffScheduleDisplayUntilDate) {
			return false;
		}
		const currentDateOnly = luxonDate.startOf("day");
		const visibilityDateOnly = chosenDepartment.staffScheduleDisplayUntilDate.startOf("day");
		return currentDateOnly > visibilityDateOnly;
	}, [luxonDate, chosenDepartment.staffScheduleDisplayUntilDate]);

	useEffect(() => {
		if (weekIndex === -1 || dayIndex === -1) {
			const homePath = chosenDepartment.department_id ? `/${chosenDepartment.department_id}/day-schedule/${DateTime.now().toFormat("yyyy-MM-dd")}` : "/";
			navigate(homePath, { replace: true });
		}
	}, [weekIndex, dayIndex, navigate, chosenDepartment.department_id]);

	useEffect(() => {
		const searchParams = new URLSearchParams(location.search);
		const requestId = searchParams.get("requestId");

		if (requestId) {
			setRequestIdToScroll(requestId);
			toggleDrawerOpen(true, RecommendationOpenSource.DayView);
			const newSearchParams = new URLSearchParams(location.search);
			newSearchParams.delete("requestId");
			const newSearch = newSearchParams.toString();
			navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ""}`, { replace: true });
		}
	}, [location, navigate, toggleDrawerOpen]);

	const { recommendations, getRecommendations, handleRemoveRecommendation, setChosenLevel, implementedChanges, setImplementedChanges, startingDate } =
		useDayScheduleStaffingIssues({ dayIndex, weekIndex });
	// DAT-906 · Returned here by "Back to wage breakdown": the view it was in, and whose breakdown to reopen.
	const reopen = useArrivalState(REOPEN_WAGE_BREAKDOWN_STATE_KEY, parseWageBreakdownReopen, null);
	// DEFAULT: Scheduled, as the page always opened, unless returning to an Actual breakdown.
	const [showActual, setShowActual] = useState<boolean>(reopen?.isActual ?? false);
	const [pendingReopen, setPendingReopen] = useState<WageBreakdownReopen | null>(reopen ?? null);
	const markReopenApplied = useCallback(() => setPendingReopen(null), []);
	const reopenState = useMemo(() => ({ pending: pendingReopen, markApplied: markReopenApplied }), [pendingReopen, markReopenApplied]);

	const availableLocations = useMemo<string[]>(() => getUniqueLocations(chosenDepartment.locations, false).map((loc) => loc.location), [chosenDepartment]);

	const selectedLocations = useMemo<string[]>(() => {
		if (availableLocations.length === 0) return [];
		return availableLocations.filter((location) => !settings.hiddenLocations.includes(location));
	}, [settings.hiddenLocations, availableLocations]);

	const activeNurses = useMemo<BaseNurseShift[]>(() => {
		if (dayIndex === -1 || !weekViewData[dayIndex]) {
			return [];
		}
		return showActual ? weekViewData[dayIndex].shiftsActual : weekViewData[dayIndex].shifts;
	}, [weekViewData, showActual, dayIndex]);

	const handleDrawerOpen = useCallback((): void => {
		getRecommendations();
		toggleDrawerOpen(true, RecommendationOpenSource.DayView);
	}, [getRecommendations, toggleDrawerOpen]);

	const handleDrawerClose = useCallback((): void => {
		toggleDrawerOpen(false, RecommendationOpenSource.DayView);
		setRequestIdToScroll(null);
	}, [toggleDrawerOpen]);

	const handleSelectedLocationsChange = useCallback(
		(updater: string[] | ((prev: string[]) => string[])) => {
			if (availableLocations.length === 0) return;

			const newSelectedLocations = typeof updater === "function" ? updater(selectedLocations) : updater;
			const hiddenLocations = availableLocations.filter((location) => !newSelectedLocations.includes(location));
			updateSettings({ hiddenLocations });
		},
		[availableLocations, selectedLocations, updateSettings],
	);

	useEffect(() => {
		if (weekIndex !== -1) {
			setWeekIndex(weekIndex);
		}
	}, [weekIndex, setWeekIndex]);

	useEffect(() => {
		setShowStaffVisibilityBanner(true);
	}, [luxonDate, chosenDepartment.staffScheduleDisplayUntilDate]);

	if (weekIndex === -1 || dayIndex === -1) {
		return null;
	}

	return (
		<Box
			data-testid="day-schedule-root"
			sx={{
				width: "100%",
				bgcolor: (theme) => (isDataInSelfSchedulingPeriod(luxonDate) ? STATUS.WARNING.light : alpha(theme.palette.primary.main, 0.08)),
				display: "flex",
				flexDirection: "column",
				// Fill the layout's content window (`ContentWrapper`) exactly. That window is flex-sized to the
				// gap between the fixed header and footer and clips its overflow, so a `vh` height here — which
				// knows nothing about that chrome — always overshoots it: `95vh` ran ~29px past the clip on a
				// 700px-tall viewport. The scroll region inside ends just as far past it, so its last rows
				// scrolled into a clipped band, unreachable at any scroll position. Zooming out only helped
				// because it shrinks the fixed chrome relative to `vh`. The height must come from the parent.
				height: "100%",
				overflow: "hidden",
			}}
		>
			<DayScheduleHeader
				selectedNurse={selectedNurse}
				setSelectedNurse={setSelectedNurse}
				drawerOpen={isOpen}
				date={luxonDate}
				weekIndex={weekIndex}
				showActual={showActual}
				dayIndex={dayIndex}
				nurses={activeNurses}
				setShowActual={setShowActual}
				keepInitialView={reopen !== undefined}
				setWeekIndex={setWeekIndex}
				handleDrawerOpen={handleDrawerOpen}
				selectedLocations={selectedLocations}
				availableLocations={availableLocations}
			/>
			<WageBreakdownReopenProvider value={reopenState}>
				<DayScheduleContentWrapper
					date={luxonDate}
					showActual={showActual}
					weekIndex={weekIndex}
					nurses={activeNurses}
					selectedNurse={selectedNurse}
					drawerOpen={isOpen}
					selectedLocations={selectedLocations}
					setSelectedLocations={handleSelectedLocationsChange}
					availableLocations={availableLocations}
				/>
			</WageBreakdownReopenProvider>
			{isOpen && (
				<RecommendationsDrawer
					open={isOpen}
					handleOpenDialog={() => {}}
					isAutoScheduler={false}
					recommendations={recommendations}
					handleDrawerClose={handleDrawerClose}
					setChosenLevel={setChosenLevel}
					implementedChanges={implementedChanges}
					setImplementedChanges={setImplementedChanges}
					weekIndex={weekIndex}
					startingDate={startingDate}
					date={luxonDate}
					handleRemoveRecommendation={handleRemoveRecommendation}
					requestId={requestIdToScroll}
				/>
			)}
			{isDraftMode && showDraftBanner && (
				<Alert
					severity="warning"
					icon={<InfoOutlined />}
					action={
						<IconButton
							data-testid="day-schedule-draft-banner-close-button"
							size="small"
							onClick={() => setShowDraftBanner(false)}
							sx={{ color: STATUS.WARNING.dark, "&:hover": { backgroundColor: alpha(STATUS.WARNING.dark, 0.08) } }}
						>
							<Close fontSize="small" />
						</IconButton>
					}
					sx={{
						position: "fixed",
						bottom: isDateNotVisibleToStaff && showStaffVisibilityBanner ? "48px" : 0,
						left: 0,
						right: isOpen ? `${DRAWER_WIDTH_IN_PX}px` : 0,
						borderRadius: 2,
						zIndex: 1300,
						backgroundColor: STATUS.WARNING.light,
						color: STATUS.WARNING.dark,
						border: `1px solid ${STATUS_BADGE.ORANGE}`,
						transition: "right 0.3s ease, bottom 0.3s ease",
						"& .MuiAlert-message": { display: "flex", alignItems: "center", width: "100%" },
					}}
				>
					<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
						<Typography sx={(theme) => ({ fontWeight: 600, color: theme.palette.primary.dark })}>{t("draft.mode.footer.attention")}</Typography>
						<Typography sx={{ color: INK.SECONDARY }}>{t("draft.mode.footer.message")}</Typography>
					</Box>
				</Alert>
			)}
			{isDateNotVisibleToStaff && showStaffVisibilityBanner && (
				<Alert
					severity="warning"
					icon={<InfoOutlined />}
					action={
						<IconButton
							data-testid="day-schedule-visibility-banner-close-button"
							size="small"
							onClick={() => setShowStaffVisibilityBanner(false)}
							sx={{ color: STATUS.WARNING.dark, "&:hover": { backgroundColor: alpha(STATUS.WARNING.dark, 0.08) } }}
						>
							<Close fontSize="small" />
						</IconButton>
					}
					sx={{
						position: "fixed",
						bottom: 0,
						left: 0,
						right: isOpen ? `${DRAWER_WIDTH_IN_PX}px` : 0,
						borderRadius: 2,
						zIndex: 1301,
						backgroundColor: STATUS.WARNING.light,
						color: STATUS.WARNING.dark,
						border: `1px solid ${STATUS_BADGE.ORANGE}`,
						transition: "right 0.3s ease",
						"& .MuiAlert-message": { display: "flex", alignItems: "center", width: "100%" },
					}}
				>
					<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
						<Typography sx={(theme) => ({ fontWeight: 600, color: theme.palette.primary.dark })}>{t("staff.schedule.visibility.attention")}</Typography>
						<Typography sx={{ color: INK.SECONDARY }}>{t("staff.schedule.visibility.message")}</Typography>
					</Box>
				</Alert>
			)}
		</Box>
	);
};
